'use client'

import Link from 'next/link'
import { MC, SERIF } from '@/lib/mycio/theme'
import { useMyCIOLanguage } from '@/lib/mycio/MyCIOLanguageContext'
import { getMyCIOCopy, getMyCIOYourDataCopy } from '@/lib/mycio/translations'
import LanguageSelect from '@/components/mycio/LanguageSelect'

/**
 * Sticky top bar (design: `MyCIO Landing.dc.html` header — 72px, bone at 0.82 + 12px blur, 1px #DED7C7).
 * Wordmark left (Newsreader 22px, ".ai" forest); centre links Why MyCIO / Tax coverage / Your data / Pricing
 * (hidden ≤640px via `.mc-navlink`); right: language selector, Sign in, "Build my plan".
 * Links are absolute (`/#why`) so the same bar works on the country coverage pages and onboarding.
 */
export default function Nav({ right }: { right?: React.ReactNode }) {
  const { language } = useMyCIOLanguage()
  const copy = getMyCIOCopy(language)
  const yourData = getMyCIOYourDataCopy(language)

  const linkStyle: React.CSSProperties = {
    fontSize: 15,
    color: MC.muted,
    textDecoration: 'none',
    letterSpacing: '0.01em',
  }

  return (
    <header className="mc-nav">
      <div
        className="mc-nav-inner mc-container"
        style={{ maxWidth: 1180, margin: '0 auto', padding: '0 32px', height: 72, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 24 }}
      >
        <Link
          href="/"
          style={{ fontFamily: SERIF, fontSize: 22, fontWeight: 500, letterSpacing: '-0.01em', color: MC.ink, textDecoration: 'none' }}
        >
          MyCIO<span style={{ color: MC.forest }}>.ai</span>
        </Link>
        <nav className="mc-nav-actions" style={{ display: 'flex', alignItems: 'center', gap: 28 }}>
          <Link href="/#why" className="mc-navlink" style={linkStyle}>{copy.nav.why}</Link>
          <Link href="/#coverage" className="mc-navlink" style={linkStyle}>{copy.nav.coverage}</Link>
          {/* the data-trust story — the objection-handler content link (section on the landing, deep dive page) */}
          <Link href="/your-data" className="mc-navlink" style={linkStyle}>{yourData.navLabel}</Link>
          <Link href="/#pricing" className="mc-navlink" style={linkStyle}>{copy.nav.pricing}</Link>

          <LanguageSelect />

          {right ?? (
            <>
              {/* "Sign in" stays reachable on phones as a compact text link (returning users must be able to
                  reach the sign-in surface, not only the "Build my plan" CTA). */}
              <Link href="/sign-in" className="mc-nav-signin mc-navlink-keep" style={linkStyle}>{copy.nav.signin}</Link>
              <Link
                href="/sign-in#signup"
                className="mc-cta-primary mc-nav-cta"
                style={{ fontSize: 15, fontWeight: 600, color: MC.bg, background: MC.forest, padding: '11px 20px', borderRadius: 999, textDecoration: 'none', whiteSpace: 'nowrap' }}
              >
                {copy.nav.cta}
              </Link>
            </>
          )}
        </nav>
      </div>
    </header>
  )
}
